import { useMemo } from "react";
import { useResource } from "../lib/hooks";
import type { Employee, Shift } from "../lib/types";

/**
 * Weekly operations report, prototype-parity: six KPI tiles, hours-by-day bars,
 * a compliance donut (pure CSS conic-gradient), hours by client/site, and top
 * staff against the 38-hour guide. Everything computed live from the roster,
 * compliance and workflow data; Export uses the browser's print-to-PDF.
 */

type Standing = "ok" | "warn" | "bad";
type DocStatus = "none" | "current" | "expiring" | "expired";
interface ComplianceData { documents: Array<{ employee_id: string; status: DocStatus }>; }
interface LeaveRow { id: string; status: string; }
interface SubmissionRow { id: string; status: string; }

const SEVERITY: Record<DocStatus, number> = { expired: 3, expiring: 2, current: 1, none: 0 };

const local = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const thisWeek = () => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: local(monday), to: local(sunday), monday };
};

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const hoursOf = (s: Shift) =>
    Math.max(0, new Date(s.ends_at).getTime() - new Date(s.starts_at).getTime()) / 3_600_000;

export const Reports = () => {
    const { from, to, monday } = thisWeek();
    const shifts = useResource<Shift[]>(`/shifts?from=${from}T00:00:00Z&to=${to}T23:59:59Z`);
    const employees = useResource<Employee[]>("/employees");
    const compliance = useResource<ComplianceData>("/compliance/documents");
    const leave = useResource<LeaveRow[]>("/leave-requests?status=pending");
    const submissions = useResource<SubmissionRow[]>("/form-submissions?status=new");

    const standing = useMemo(() => {
        const worst = new Map<string, DocStatus>();
        for (const d of compliance.data?.documents ?? []) {
            const seen = worst.get(d.employee_id);
            if (!seen || SEVERITY[d.status] > SEVERITY[seen]) worst.set(d.employee_id, d.status);
        }
        const out = new Map<string, Standing>();
        for (const [id, st] of worst) out.set(id, st === "expired" ? "bad" : st === "expiring" ? "warn" : "ok");
        return out;
    }, [compliance.data]);

    const stats = useMemo(() => {
        const rows = (shifts.data ?? []).filter((s) => s.status !== "cancelled");
        const active = (employees.data ?? []).filter((e) => e.employment_status === "active");
        const now = Date.now();

        const totalHours = rows.reduce((a, s) => a + hoursOf(s), 0);
        const workedHours = rows
            .filter((s) => new Date(s.ends_at).getTime() <= now)
            .reduce((a, s) => a + hoursOf(s), 0);
        const open = rows.filter((s) => !s.employee_id).length;
        const filled = rows.length ? ((rows.length - open) / rows.length) * 100 : 100;

        const comp = { ok: 0, warn: 0, bad: 0 };
        for (const e of active) comp[standing.get(e.id) ?? "ok"] += 1;
        const compliantPct = active.length ? (comp.ok / active.length) * 100 : 100;

        const byDay = DAY_LABELS.map((label, i) => {
            const day = new Date(monday);
            day.setDate(monday.getDate() + i);
            const key = local(day);
            const hours = rows
                .filter((s) => local(new Date(s.starts_at)) === key)
                .reduce((a, s) => a + hoursOf(s), 0);
            return { label, hours };
        });

        // Where the care hours go: the participant when placed with a person,
        // else the location — the prototype's client/site rollup.
        const bySite = new Map<string, number>();
        for (const s of rows) {
            const key = s.participant_name ?? s.location_name ?? "Unassigned";
            bySite.set(key, (bySite.get(key) ?? 0) + hoursOf(s));
        }
        const sites = [...bySite.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);

        const perStaff = active
            .map((e) => ({
                id: e.id, name: e.full_name,
                hours: rows.filter((s) => s.employee_id === e.id).reduce((a, s) => a + hoursOf(s), 0),
            }))
            .filter((r) => r.hours > 0)
            .sort((a, b) => b.hours - a.hours)
            .slice(0, 8);

        return { totalHours, workedHours, open, filled, comp, compliantPct, byDay, sites, perStaff };
    }, [shifts.data, employees.data, standing, monday]);

    const maxDay = Math.max(1, ...stats.byDay.map((d) => d.hours));
    const maxSite = Math.max(1, ...stats.sites.map(([, h]) => h));
    const compTotal = Math.max(1, stats.comp.ok + stats.comp.warn + stats.comp.bad);
    const okDeg = (stats.comp.ok / compTotal) * 360;
    const warnDeg = okDeg + (stats.comp.warn / compTotal) * 360;

    const pendingLeave = (leave.data ?? []).length;
    const newSubmissions = (submissions.data ?? []).length;

    return (
        <div>
            <div className="toolbar" style={{ marginBottom: "0.6rem", padding: 0 }}>
                <span className="muted">This week at a glance — live from roster, time clock and compliance data</span>
                <div className="spacer" />
                <button className="btn ghost" onClick={() => window.print()}>⤓ Export PDF</button>
            </div>

            <div className="stat-strip">
                <div className="stat good"><div className="num">{stats.totalHours.toFixed(0)}</div><div className="lbl">Hours rostered</div></div>
                <div className="stat good"><div className="num">{stats.workedHours.toFixed(0)}</div><div className="lbl">Hours worked (to date)</div></div>
                <div className={`stat ${stats.filled < 90 ? "warn" : "good"}`}><div className="num">{stats.filled.toFixed(0)}%</div><div className="lbl">Shift fill rate</div></div>
                <div className={`stat ${stats.compliantPct < 80 ? "warn" : "good"}`}><div className="num">{stats.compliantPct.toFixed(0)}%</div><div className="lbl">Staff fully compliant</div></div>
                <div className={`stat ${pendingLeave ? "warn" : "good"}`}><div className="num">{pendingLeave}</div><div className="lbl">Leave requests pending</div></div>
                <div className={`stat ${newSubmissions ? "warn" : "good"}`}><div className="num">{newSubmissions}</div><div className="lbl">Form submissions to review</div></div>
            </div>

            <div className="grid cols-2">
                <div className="card">
                    <div className="card-head"><h2>Rostered hours by day</h2></div>
                    <div className="card-body">
                        <div className="bar-chart">
                            {stats.byDay.map((d) => (
                                <div key={d.label} className="bar-col">
                                    <span className="muted">{d.hours.toFixed(0)}</span>
                                    <div className="bar" style={{ height: `${(d.hours / maxDay) * 100}%` }} />
                                    <span className="muted">{d.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-head"><h2>Compliance status</h2></div>
                    <div className="card-body donut-flex">
                        <div className="donut" style={{
                            background: `conic-gradient(var(--pass) 0deg ${okDeg}deg, var(--warn) ${okDeg}deg ${warnDeg}deg, var(--block) ${warnDeg}deg 360deg)`,
                        }}>
                            <div className="donut-hole">
                                <b>{stats.compliantPct.toFixed(0)}%</b>
                                <span>compliant</span>
                            </div>
                        </div>
                        <div className="donut-legend">
                            <span><i style={{ background: "var(--pass)" }} /> Compliant — {stats.comp.ok}</span>
                            <span><i style={{ background: "var(--warn)" }} /> Expiring &lt; 60 days — {stats.comp.warn}</span>
                            <span><i style={{ background: "var(--block)" }} /> Expired — {stats.comp.bad}</span>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-head"><h2>Hours by client / site</h2></div>
                    <div className="card-body">
                        <p className="muted" style={{ marginBottom: "0.5rem" }}>Where the week's care hours go</p>
                        {stats.sites.map(([name, hours]) => (
                            <div key={name} className="person-row">
                                <div className="person-main"><div className="name">{name}</div></div>
                                <div className="meter" style={{ width: 200 }}>
                                    <div className="meter-track">
                                        <div className="meter-fill" style={{ width: `${(hours / maxSite) * 100}%` }} />
                                    </div>
                                    <span className="meter-value">{hours.toFixed(0)} h</span>
                                </div>
                            </div>
                        ))}
                        {!stats.sites.length && <div className="empty">Nothing rostered this week.</div>}
                    </div>
                </div>

                <div className="card">
                    <div className="card-head"><h2>Top staff by rostered hours</h2></div>
                    <div className="card-body">
                        <p className="muted" style={{ marginBottom: "0.5rem" }}>Against the 38-hour guide</p>
                        {stats.perStaff.map((r) => (
                            <div key={r.id} className="person-row">
                                <div className="person-main"><div className="name">{r.name}</div></div>
                                <div className="meter" style={{ width: 200 }}>
                                    <div className="meter-track">
                                        <div className="meter-fill" style={{
                                            width: `${Math.min(100, (r.hours / 38) * 100)}%`,
                                            background: r.hours > 38 ? "var(--block)" : undefined,
                                        }} />
                                    </div>
                                    <span className="meter-value">{r.hours.toFixed(1)} h</span>
                                </div>
                            </div>
                        ))}
                        {!stats.perStaff.length && <div className="empty">Nothing rostered this week.</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};
